// MarketWrapService.js
// Reads from Firestore marketWraps collection (written by cloud-function-roundup)
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    doc,
    getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

const COLLECTION = 'marketWraps';
const DEFAULT_LIMIT = 10;

class MarketWrapService {
    /**
     * Fetch the latest visible wraps of a given type.
     * @param {'daily'|'weekly'} wrapType
     * @param {number} [limitCount]
     * @returns {Promise<Array>} Array of wrap docs with id field added
     */
    async getWraps(wrapType, limitCount = DEFAULT_LIMIT) {
        try {
            const q = query(
                collection(db, COLLECTION),
                where('visible', '==', true),
                where('type', '==', wrapType),
                orderBy('createdAt', 'desc'),
                limit(limitCount)
            );

            const snapshot = await getDocs(q);

            return snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...this._convertTimestamps(docSnap.data()),
            }));
        } catch (error) {
            console.error(`[MarketWrapService] Error fetching ${wrapType} wraps:`, error);
            throw error;
        }
    }

    /**
     * Fetch a single wrap document by its ID.
     * @param {string} wrapId  e.g. '2026-03-03_daily'
     * @returns {Promise<Object|null>}
     */
    async getWrapById(wrapId) {
        try {
            const docRef = doc(db, COLLECTION, wrapId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) return null;
            return {
                id: docSnap.id,
                ...this._convertTimestamps(docSnap.data()),
            };
        } catch (error) {
            console.error(`[MarketWrapService] Error fetching wrap ${wrapId}:`, error);
            throw error;
        }
    }

    /**
     * Fetch the most recent daily roundup.
     * @returns {Promise<Object|null>}
     */
    async getLatestDaily() {
        const results = await this.getWraps('daily', 1);
        return results.length > 0 ? results[0] : null;
    }

    /**
     * Fetch the most recent weekly wrap.
     * @returns {Promise<Object|null>}
     */
    async getLatestWeekly() {
        const results = await this.getWraps('weekly', 1);
        return results.length > 0 ? results[0] : null;
    }

    /**
     * Convert Firestore Timestamps to ISO strings for safe JSON usage.
     * @param {Object} data
     * @returns {Object}
     */
    _convertTimestamps(data) {
        const converted = { ...data };
        for (const key of ['createdAt', 'updatedAt']) {
            if (converted[key]?.toDate) {
                converted[key] = converted[key].toDate().toISOString();
            }
        }
        return converted;
    }
}

// Singleton export (same pattern as NewsCacheService)
let _instance = null;
export const getMarketWrapService = () => {
    if (!_instance) _instance = new MarketWrapService();
    return _instance;
};

export default MarketWrapService;
